import { useRegisterSW } from "virtual:pwa-register/vue"
import { ref, computed } from "vue";

const COOLDOWN_KEY = 'sw-reload-cooldown'
const RELOADING_KEY = 'sw-reloading'
const COOLDOWN_DURATION = 60 * 1000 // 1 minutes

// Singleton state - shared across all instances
const needRefresh = ref(false);
const isReloading = ref(false);
const offlineReady = ref(false);
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | null = null;
let updateInterval: NodeJS.Timeout | null = null;
let isInitialized = false;

// Check if we're in cooldown period
const isInCooldown = (): boolean => {
  const lastAction = localStorage.getItem(COOLDOWN_KEY);
  if (!lastAction) return false;

  const lastActionTime = parseInt(lastAction, 10);
  const now = Date.now();
  return (now - lastActionTime) < COOLDOWN_DURATION;
};

// Set cooldown timestamp
const setCooldown = (): void => {
  localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
};

// Clear cooldown (used when reload is successful)
const clearCooldown = (): void => {
  localStorage.removeItem(COOLDOWN_KEY);
  localStorage.removeItem(RELOADING_KEY);
};

// Set reloading state in localStorage
const setReloading = (): void => {
  localStorage.setItem(RELOADING_KEY, Date.now().toString());
};

// Check if we're currently in a reload state (from localStorage)
const isCurrentlyReloading = (): boolean => {
  const reloadingTime = localStorage.getItem(RELOADING_KEY);
  if (!reloadingTime) return false;

  const reloadStartTime = parseInt(reloadingTime, 10);
  const now = Date.now();
  // Consider reloading for up to 30 seconds after reload was initiated
  return (now - reloadStartTime) < 10000;
};

// Initialize reloading state from localStorage
const initializeReloadingState = (): void => {
  if (isCurrentlyReloading()) {
    isReloading.value = true;

    // Set up a timer to clear the reloading state after cooldown
    const reloadingTime = localStorage.getItem(RELOADING_KEY);
    if (reloadingTime) {
      const reloadStartTime = parseInt(reloadingTime, 10);
      const elapsed = Date.now() - reloadStartTime;
      const remaining = Math.max(0, 10000 - elapsed); // 30 seconds max
      console.log('Remaining time to clear reloading state:', remaining);
      setTimeout(() => {
        console.log('Clearing reloading state...');
        isReloading.value = false;
        clearCooldown();
      }, remaining);
    }
  }
};

// Only show prompt if we need refresh and not in cooldown
const shouldShowPrompt = computed(() => {
  return needRefresh.value && !isInCooldown();
});

// Initialize service worker registration (only once)
const initializeServiceWorker = (intervalMS: number = 1 * 60 * 1000) => {
  if (isInitialized) return;

  try {
    const swRegistration = useRegisterSW({
      onRegistered(r: ServiceWorkerRegistration) {
        if (r) {
          // Note: Don't start watching here automatically
          // Let the component manage the lifecycle

          r.addEventListener('updatefound', () => {
            const newWorker = r.installing
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // Only show if not in cooldown
                  if (!isInCooldown()) {
                    needRefresh.value = true
                  }
                }
              })
            }
          })
        }
      },
      onNeedRefresh() {
        // Only show if not in cooldown
        if (!isInCooldown()) {
          needRefresh.value = true;
        }
      },
    });

    offlineReady.value = swRegistration.offlineReady.value;
    updateServiceWorker = swRegistration.updateServiceWorker;
    isInitialized = true;
  } catch (error) {
    console.warn('Failed to initialize service worker, will retry when component mounts:', error);
    // Don't set isInitialized to true so it can be retried
  }
};

// Start watching for updates
const startWatching = (registration: ServiceWorkerRegistration, intervalMS: number) => {
  if (updateInterval) return; // Already watching

  updateInterval = setInterval(() => {
    registration.update();
  }, intervalMS);
};

// Stop watching for updates
const stopWatching = () => {
  if (updateInterval) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
};

const reload = async () => {
  if (isReloading.value) return;

  isReloading.value = true;
  setReloading(); // Persist reloading state to localStorage
  setCooldown();
  try {
    // Check if service worker is available
    if (!updateServiceWorker) {
      console.warn('Service worker not available, attempting to reload anyway...');
      // If service worker isn't available, just reload the page
      window.location.reload();
      return;
    }

    // Update the service worker
    await updateServiceWorker(true);

    // Wait for the new service worker to take control
    await new Promise<void>((resolve) => {
      const checkController = () => {
        if (navigator.serviceWorker.controller) {
          // Wait a bit more to ensure the new SW is fully active
          setTimeout(resolve, 500);
        } else {
          setTimeout(checkController, 100);
        }
      };
      checkController();
    });

    // Clear cooldown since we're successfully reloading
    //clearCooldown();

    // Reload the page
    setTimeout(() => {
      window.location.reload();
    }, 5000);
  } catch (error) {
    console.error('Failed to update service worker:', error);
    isReloading.value = false;
    localStorage.removeItem(RELOADING_KEY); // Clear reloading state on error
    // Set cooldown even on error to prevent spam
    setCooldown();
    close();
  }
};

const close = async () => {
  // Set cooldown when user dismisses the prompt
  setCooldown();

  offlineReady.value = false;
  needRefresh.value = false;
  isReloading.value = false;
};

// Track if we've initialized the reloading state
let reloadingStateInitialized = false;

export default function useServiceWorkerReload(intervalMS: number = 1 * 60 * 1000) {
  // Initialize reloading state from localStorage on first use
  if (!reloadingStateInitialized) {
    initializeReloadingState();
    reloadingStateInitialized = true;
  }

  // Initialize service worker if not already done
  initializeServiceWorker(intervalMS);

  return {
    needRefresh,
    offlineReady,
    isReloading,
    shouldShowPrompt,
    reload,
    close,
    startWatching: (registration: ServiceWorkerRegistration) => startWatching(registration, intervalMS),
    stopWatching
  };
}
